import { CommonModule } from '@angular/common';
import { Component } from '@angular/core';
import {
  FormBuilder,
  FormsModule,
  ReactiveFormsModule,
  Validators,
} from '@angular/forms';

interface CreationTag {
  label: string;
}

interface CreationItem {
  title: string;
  description: string;
  altText: string;
  featured: boolean;
  tags: string[];
  imagePreview: string | ArrayBuffer | null;
  fileName?: string;
}

@Component({
  selector: 'app-creations-manager',
  standalone: true,
  imports: [CommonModule, ReactiveFormsModule, FormsModule],
  templateUrl: './creations-manager.component.html',
  styleUrl: './creations-manager.component.scss',
})
export class CreationsManagerComponent {
  selectedFile: File | null = null;
  imagePreview: string | ArrayBuffer | null = null;

  availableTags: string[] = [
    'Media Walls',
    'Drinks Bars',
    'Home Offices',
    'Wardrobes',
    'Commercial Spaces',
  ];

  selectedTag = '';

  creationForm = this.fb.group({
    title: ['', [Validators.required]],
    description: [''],
    altText: ['', [Validators.required]],
    featured: [false],
  });

  tags: CreationTag[] = [{ label: 'Media Walls' }];

  existingCreations: CreationItem[] = [
    {
      title: 'Walnut Media Wall',
      description: 'A bespoke fitted media wall with warm walnut tones.',
      altText: 'Walnut fitted media wall with shelving and soft lighting',
      featured: true,
      tags: ['Media Walls'],
      imagePreview: null,
      fileName: 'walnut-media-wall.jpg',
    },
    {
      title: 'Modern Slatted Divider',
      description: 'Clean slatted divider used to zone an open-plan room.',
      altText: 'Modern timber slatted divider in an open plan interior',
      featured: false,
      tags: ['Drinks Bars'],
      imagePreview: null,
      fileName: 'slatted-divider.jpg',
    },
  ];

  constructor(private fb: FormBuilder) {}

  onFileSelected(event: Event): void {
    const input = event.target as HTMLInputElement;
    const file = input.files?.[0] || null;

    if (!file) return;

    this.selectedFile = file;

    const reader = new FileReader();
    reader.onload = () => {
      this.imagePreview = reader.result;
    };
    reader.readAsDataURL(file);
  }

  addTag(): void {
    const value = this.selectedTag.trim();

    if (!value) return;

    const alreadyExists = this.tags.some(
      (tag) => tag.label.toLowerCase() === value.toLowerCase(),
    );

    if (!alreadyExists) {
      this.tags.push({ label: value });
    }

    this.selectedTag = '';
  }

  removeTag(index: number): void {
    this.tags.splice(index, 1);
  }

  get availableTagOptions(): string[] {
    return this.availableTags.filter(
      (tag) =>
        !this.tags.some(
          (selectedTag) =>
            selectedTag.label.toLowerCase() === tag.toLowerCase(),
        ),
    );
  }

  submitCreation(): void {
    if (this.creationForm.invalid || !this.selectedFile) {
      this.creationForm.markAllAsTouched();
      return;
    }

    const newCreation: CreationItem = {
      title: this.creationForm.value.title || '',
      description: this.creationForm.value.description || '',
      altText: this.creationForm.value.altText || '',
      featured: !!this.creationForm.value.featured,
      tags: this.tags.map((tag) => tag.label),
      imagePreview: this.imagePreview,
      fileName: this.selectedFile.name,
    };

    this.existingCreations.unshift(newCreation);

    this.creationForm.reset({
      title: '',
      description: '',
      altText: '',
      featured: false,
    });

    this.tags = [];
    this.selectedTag = '';
    this.selectedFile = null;
    this.imagePreview = null;
  }

  trackByTag(index: number, tag: CreationTag): string {
    return tag.label;
  }

  trackByCreation(index: number, item: CreationItem): string {
    return `${item.title}-${index}`;
  }
}
